import { useMemo, useState } from "react";
import clsx from "clsx";
import { Lock, Trash2, Unlock } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  layerDefaultFill,
  PITCH_FILL_PRESETS,
  PLAN_LAYERS,
  pointInPolygon,
  polygonArea,
  textDuplicatesShapeLabel,
  type PlanLayer,
  type PlanRoute,
  type PlanShape,
  type PlanSymbol,
  type PlanText,
  type SitePlanVectorDocument,
} from "@/lib/sitePlanDocument";
import {
  getSymbolDefinition,
  SITE_PLAN_SYMBOLS,
  SitePlanSymbolPreview,
  SYMBOL_CATEGORY_LABELS,
  symbolsByCategory,
} from "@/lib/sitePlanSymbols";
import type { Pitch } from "@/types/camping";
import type { EditorSelection, EditorTool } from "./SitePlanVectorEditor";

type SidebarTab = "selection" | "symbols" | "pitches";

interface SitePlanEditorSidebarProps {
  document: SitePlanVectorDocument;
  selection: EditorSelection | null;
  onSelectionChange: (selection: EditorSelection | null) => void;
  tool: EditorTool;
  onToolChange: (tool: EditorTool) => void;
  pitches: Pitch[];
  canEdit: boolean;
  onUpdateShape: (id: string, patch: Partial<PlanShape>) => void;
  onUpdateSymbol: (id: string, patch: Partial<PlanSymbol>) => void;
  onUpdateText: (id: string, patch: Partial<PlanText>) => void;
  onUpdateRoute: (id: string, patch: Partial<PlanRoute>) => void;
  onDelete: (selection: EditorSelection) => void;
  revealedPitchId: string | null;
  onRevealPitch: (pitchId: string) => void;
  onHideApproach: () => void;
  onAutoMatch: () => void;
  autoMatching: boolean;
}

const INPUT = "eh-input min-h-[38px] w-full text-sm";
const LABEL = "flex flex-col gap-1 text-xs font-medium text-label-secondary";

function colorForInput(value: string | null, fallback: string): string {
  const v = value ?? fallback;
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v : fallback;
}

export function SitePlanEditorSidebar({
  document: doc,
  selection,
  onSelectionChange,
  tool,
  onToolChange,
  pitches,
  canEdit,
  onUpdateShape,
  onUpdateSymbol,
  onUpdateText,
  onUpdateRoute,
  onDelete,
  revealedPitchId,
  onRevealPitch,
  onHideApproach,
  onAutoMatch,
  autoMatching,
}: SitePlanEditorSidebarProps) {
  const [tab, setTab] = useState<SidebarTab>("selection");
  const [pitchFilter, setPitchFilter] = useState("");

  const effectiveTab: SidebarTab =
    tab === "selection" && !selection ? (tool.kind === "symbol" ? "symbols" : "pitches") : tab;

  const assignedPitchIds = useMemo(
    () => new Set(doc.shapes.map((s) => s.pitch_id).filter((id): id is string => Boolean(id))),
    [doc.shapes],
  );
  const shapeByPitchId = useMemo(() => {
    const map = new Map<string, PlanShape>();
    for (const s of doc.shapes) if (s.pitch_id) map.set(s.pitch_id, s);
    return map;
  }, [doc.shapes]);

  const unassignedPitches = useMemo(
    () => pitches.filter((p) => !assignedPitchIds.has(p.id)),
    [pitches, assignedPitchIds],
  );
  const filteredPitches = useMemo(() => {
    const q = pitchFilter.trim().toLowerCase();
    if (!q) return pitches;
    return pitches.filter(
      (p) => p.code.toLowerCase().includes(q) || (p.label ?? "").toLowerCase().includes(q),
    );
  }, [pitches, pitchFilter]);

  const unassignedPitchShapes = doc.shapes.filter((s) => s.layer === "pitch" && !s.pitch_id).length;

  const selectedShape = selection?.kind === "shape" ? doc.shapes.find((s) => s.id === selection.id) ?? null : null;
  const selectedSymbol = selection?.kind === "symbol" ? doc.symbols.find((s) => s.id === selection.id) ?? null : null;
  const selectedText = selection?.kind === "text" ? doc.texts.find((t) => t.id === selection.id) ?? null : null;
  const selectedRoute =
    selection?.kind === "route" ? (doc.routes ?? []).find((r) => r.id === selection.id) ?? null : null;

  const tabs: { key: SidebarTab; label: string }[] = [
    { key: "selection", label: "Auswahl" },
    { key: "symbols", label: "Symbole" },
    { key: "pitches", label: `Plätze (${unassignedPitches.length} offen)` },
  ];

  return (
    <aside className="flex min-h-0 w-full flex-col rounded-xl border border-separator bg-card lg:w-80 lg:shrink-0">
      <div className="flex border-b border-separator text-sm">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={clsx(
              "flex-1 px-2 py-2.5 font-medium transition",
              effectiveTab === t.key
                ? "border-b-2 border-accent text-label"
                : "text-label-secondary hover:text-label",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {effectiveTab === "selection" ? (
          <>
            {!selection ? (
              <p className="text-sm text-label-secondary">
                Wählen Sie eine Fläche, ein Symbol, einen Text oder eine sichtbare Anfahrt im Plan aus.
              </p>
            ) : null}

            {selectedShape ? (
              <ShapeProperties
                shape={selectedShape}
                pitches={pitches}
                texts={doc.texts}
                shapeByPitchId={shapeByPitchId}
                canEdit={canEdit}
                onUpdate={(patch) => onUpdateShape(selectedShape.id, patch)}
                onDelete={() => onDelete({ kind: "shape", id: selectedShape.id })}
                onDeleteText={(id) => onDelete({ kind: "text", id })}
                routes={doc.routes ?? []}
                revealedPitchId={revealedPitchId}
                onRevealPitch={onRevealPitch}
                onHideApproach={onHideApproach}
                onDrawRoute={(pitchId) => onToolChange({ kind: "route", pitchId })}
                onSelectRoute={(id) => onSelectionChange({ kind: "route", id })}
              />
            ) : null}

            {selectedSymbol ? (
              <SymbolProperties
                symbol={selectedSymbol}
                canEdit={canEdit}
                onUpdate={(patch) => onUpdateSymbol(selectedSymbol.id, patch)}
                onDelete={() => onDelete({ kind: "symbol", id: selectedSymbol.id })}
              />
            ) : null}

            {selectedRoute ? (
              <RouteProperties
                route={selectedRoute}
                pitches={pitches}
                canEdit={canEdit}
                onUpdate={(patch) => onUpdateRoute(selectedRoute.id, patch)}
                onDelete={() => onDelete({ kind: "route", id: selectedRoute.id })}
              />
            ) : null}

            {selectedText ? (
              <TextProperties
                text={selectedText}
                canEdit={canEdit}
                onUpdate={(patch) => onUpdateText(selectedText.id, patch)}
                onDelete={() => onDelete({ kind: "text", id: selectedText.id })}
              />
            ) : null}
          </>
        ) : null}

        {effectiveTab === "symbols" ? (
          <div className="space-y-4">
            <p className="text-xs text-label-secondary">
              Symbol wählen und dann auf den Plan klicken. Mehrfaches Platzieren möglich, Esc beendet.
            </p>
            {symbolsByCategory().map((group) => (
              <div key={group.category}>
                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                  {SYMBOL_CATEGORY_LABELS[group.category]}
                </h4>
                <div className="grid grid-cols-4 gap-1.5">
                  {group.items.map((def) => {
                    const active = tool.kind === "symbol" && tool.type === def.type;
                    return (
                      <button
                        key={def.type}
                        type="button"
                        disabled={!canEdit}
                        title={def.label}
                        onClick={() => onToolChange(active ? { kind: "select" } : { kind: "symbol", type: def.type })}
                        className={clsx(
                          "flex flex-col items-center gap-1 rounded-lg border px-1 py-2 text-[10px] leading-tight transition disabled:opacity-50",
                          active
                            ? "border-accent bg-accent-soft text-label"
                            : "border-separator text-label-secondary hover:bg-fill-tertiary",
                        )}
                      >
                        <SitePlanSymbolPreview type={def.type} size={26} />
                        <span className="line-clamp-2 text-center">{def.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {effectiveTab === "pitches" ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-fill-tertiary/60 p-2.5 text-xs text-label-secondary">
              <div className="flex justify-between">
                <span>Stellplätze zugeordnet</span>
                <span className="font-semibold text-label">
                  {assignedPitchIds.size} / {pitches.length}
                </span>
              </div>
              <div className="mt-1 flex justify-between">
                <span>Platzflächen ohne Zuordnung</span>
                <span className={clsx("font-semibold", unassignedPitchShapes ? "text-status-orange" : "text-label")}>
                  {unassignedPitchShapes}
                </span>
              </div>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-full"
                loading={autoMatching}
                disabled={unassignedPitchShapes === 0}
                onClick={onAutoMatch}
              >
                Nach Beschriftung automatisch zuordnen
              </Button>
            ) : null}
            <input
              className={INPUT}
              placeholder="Platz suchen…"
              value={pitchFilter}
              onChange={(e) => setPitchFilter(e.target.value)}
            />
            <p className="text-[11px] text-label-tertiary">
              {selectedShape && canEdit
                ? "Klick auf einen Platz ordnet ihn der ausgewählten Fläche zu."
                : "Klick auf einen zugeordneten Platz wählt seine Fläche aus."}
            </p>
            <ul className="divide-y divide-separator text-sm">
              {filteredPitches.map((pitch) => {
                const shape = shapeByPitchId.get(pitch.id);
                const assigned = Boolean(shape);
                const isSelectedTarget = selectedShape && shape?.id === selectedShape.id;
                return (
                  <li key={pitch.id}>
                    <button
                      type="button"
                      className={clsx(
                        "flex w-full items-center justify-between gap-2 px-1 py-1.5 text-left hover:bg-fill-tertiary",
                        isSelectedTarget && "bg-accent-soft",
                      )}
                      onClick={() => {
                        if (selectedShape && canEdit && !selectedShape.locked) {
                          const release = assigned && shape?.id === selectedShape.id;
                          onUpdateShape(selectedShape.id, {
                            pitch_id: release ? null : pitch.id,
                            layer: "pitch",
                            ...(release ? {} : { use_category_color: true }),
                          });
                          return;
                        }
                        if (shape) onSelectionChange({ kind: "shape", id: shape.id });
                      }}
                    >
                      <span className="min-w-0 truncate">
                        <span className="font-medium text-label">{pitch.code}</span>
                        {pitch.label ? <span className="ml-1 text-label-tertiary">{pitch.label}</span> : null}
                      </span>
                      <span
                        className={clsx(
                          "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                          assigned ? "bg-status-green/15 text-status-green" : "bg-status-orange/15 text-status-orange",
                        )}
                      >
                        {assigned ? "zugeordnet" : "offen"}
                      </span>
                    </button>
                  </li>
                );
              })}
              {filteredPitches.length === 0 ? (
                <li className="py-2 text-xs text-label-tertiary">Keine Plätze gefunden.</li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

// --- Eigenschaften -----------------------------------------------------------------

function ShapeProperties({
  shape,
  pitches,
  texts,
  shapeByPitchId,
  canEdit,
  onUpdate,
  onDelete,
  onDeleteText,
  routes,
  revealedPitchId,
  onRevealPitch,
  onHideApproach,
  onDrawRoute,
  onSelectRoute,
}: {
  shape: PlanShape;
  pitches: Pitch[];
  texts: PlanText[];
  shapeByPitchId: ReadonlyMap<string, PlanShape>;
  canEdit: boolean;
  onUpdate: (patch: Partial<PlanShape>) => void;
  onDelete: () => void;
  onDeleteText: (id: string) => void;
  routes: PlanRoute[];
  revealedPitchId: string | null;
  onRevealPitch: (pitchId: string) => void;
  onHideApproach: () => void;
  onDrawRoute: (pitchId: string) => void;
  onSelectRoute: (id: string) => void;
}) {
  const disabled = !canEdit || shape.locked;
  const assignedPitch = shape.pitch_id ? pitches.find((p) => p.id === shape.pitch_id) : null;
  const categoryColor = assignedPitch?.category_color?.trim() || null;
  const followsCategory = Boolean(assignedPitch && categoryColor && shape.use_category_color !== false);
  const shownFill = followsCategory && categoryColor ? categoryColor : shape.fill;
  const area = Math.round(polygonArea(shape.points));
  const textsHere = texts.filter((text) => pointInPolygon({ x: text.x, y: text.y }, shape.points));
  const approach = shape.pitch_id ? routes.filter((r) => r.pitch_id === shape.pitch_id) : [];
  const approachVisible = Boolean(shape.pitch_id && revealedPitchId === shape.pitch_id);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-label">Fläche</h3>
        <div className="flex items-center gap-1">
          {canEdit ? (
            <button
              type="button"
              className="rounded-md p-1.5 text-label-secondary hover:bg-fill-tertiary"
              title={shape.locked ? "Entsperren" : "Sperren (nicht verschiebbar)"}
              onClick={() => onUpdate({ locked: !shape.locked })}
            >
              {shape.locked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
            </button>
          ) : null}
          {canEdit && !shape.locked ? (
            <button
              type="button"
              className="rounded-md p-1.5 text-status-red hover:bg-fill-tertiary"
              title="Fläche löschen"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      <label className={LABEL}>
        Stellplatz
        <select
          className={INPUT}
          disabled={disabled}
          value={shape.pitch_id ?? ""}
          onChange={(e) =>
            onUpdate({
              pitch_id: e.target.value || null,
              layer: e.target.value ? "pitch" : shape.layer,
              ...(e.target.value ? { use_category_color: true } : {}),
            })
          }
        >
          <option value="">– nicht zugeordnet –</option>
          {pitches.map((p) => {
            const takenBy = shapeByPitchId.get(p.id);
            const takenElsewhere = Boolean(takenBy && takenBy.id !== shape.id);
            return (
              <option key={p.id} value={p.id} disabled={takenElsewhere}>
                {p.code}
                {p.label ? ` – ${p.label}` : ""}
                {takenElsewhere ? " (bereits zugeordnet)" : ""}
              </option>
            );
          })}
        </select>
        {assignedPitch ? (
          <span className="text-[11px] text-label-tertiary">
            Beim Speichern wird die Fläche als Platzpolygon von {assignedPitch.code} übernommen.
          </span>
        ) : null}
      </label>

      <div className="space-y-2 rounded-lg border border-separator p-2">
        <p className="text-xs font-medium text-label">Anfahrt</p>
        {shape.pitch_id && assignedPitch ? (
          <>
            <p className="text-[11px] text-label-tertiary">
              {approach.length === 0
                ? `Noch keine Anfahrt für ${assignedPitch.code}.`
                : `${approach.length} Anfahrt${approach.length === 1 ? "" : "en"} für ${assignedPitch.code}. Zugeordnete Linien sind ausgeblendet, bis Sie sie hier einblenden.`}
            </p>
            {approach.length > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => (approachVisible ? onHideApproach() : onRevealPitch(shape.pitch_id!))}
              >
                {approachVisible ? "Anfahrt ausblenden" : "Anfahrt anzeigen"}
              </Button>
            ) : null}
            {approachVisible
              ? approach.map((route, index) => (
                  <button
                    key={route.id}
                    type="button"
                    className="block w-full rounded-md px-2 py-1.5 text-left text-xs text-label hover:bg-fill-tertiary"
                    onClick={() => onSelectRoute(route.id)}
                  >
                    Linie {index + 1} · {route.points.length} Punkte
                  </button>
                ))
              : null}
            {canEdit ? (
              <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={() => onDrawRoute(shape.pitch_id!)}>
                Neue Anfahrt zeichnen
              </Button>
            ) : null}
          </>
        ) : (
          <p className="text-[11px] text-label-tertiary">
            Ordnen Sie die Fläche einem Platz zu. Eine Anfahrt ohne Platz bleibt sichtbar; sobald sie einem Platz gehört, blendet sie sich aus und lässt sich über diese Fläche wieder einblenden.
          </p>
        )}
      </div>

      <label className={LABEL}>
        Beschriftung
        <input
          className={INPUT}
          disabled={disabled}
          value={shape.label ?? ""}
          placeholder={assignedPitch ? assignedPitch.code : "z. B. Platznummer"}
          onChange={(e) => onUpdate({ label: e.target.value || null, show_label: true })}
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-label-secondary">
        <input
          type="checkbox"
          disabled={disabled}
          checked={shape.show_label !== false}
          onChange={(e) => onUpdate({ show_label: e.target.checked })}
        />
        Nummer auf der Fläche anzeigen
      </label>
      {textsHere.length > 0 ? (
        <div className="space-y-1.5 rounded-lg border border-separator p-2">
          <p className="text-[11px] font-medium text-label-secondary">
            Texte auf dieser Fläche – die erkannte Beschriftung liegt zusätzlich auf der Nummer und lässt sich hier entfernen.
          </p>
          <ul className="space-y-1">
            {textsHere.map((text) => {
              const duplicate = textDuplicatesShapeLabel(text, shape, assignedPitch?.code ?? null);
              return (
                <li key={text.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate text-label">
                    „{text.text}“
                    {duplicate ? <span className="ml-1 text-status-orange">überlagert die Nummer</span> : null}
                  </span>
                  {canEdit ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-md px-1.5 py-1 text-status-red hover:bg-fill-tertiary"
                      onClick={() => onDeleteText(text.id)}
                    >
                      Entfernen
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <label className={LABEL}>
        Ebene
        <select
          className={INPUT}
          disabled={disabled}
          value={shape.layer}
          onChange={(e) => {
            const layer = e.target.value as PlanLayer;
            onUpdate({ layer, fill: shape.fill === layerDefaultFill(shape.layer) ? layerDefaultFill(layer) : shape.fill });
          }}
        >
          {PLAN_LAYERS.map((l) => (
            <option key={l.key} value={l.key}>
              {l.label}
            </option>
          ))}
        </select>
      </label>

      <div className={LABEL}>
        Füllfarbe
        {assignedPitch ? (
          <label className="flex items-center gap-2 font-normal text-label">
            <input
              type="checkbox"
              disabled={disabled || !categoryColor}
              checked={followsCategory}
              onChange={(e) => onUpdate({ use_category_color: e.target.checked })}
            />
            <span
              className="h-3.5 w-3.5 shrink-0 rounded-full border border-separator"
              style={{ background: categoryColor ?? "#d1d5db" }}
            />
            Farbe der Kategorie{assignedPitch.category_name ? ` „${assignedPitch.category_name}“` : ""}
          </label>
        ) : null}
        {assignedPitch && !categoryColor ? (
          <span className="font-normal text-label-tertiary">Diese Kategorie hat keine Farbe hinterlegt.</span>
        ) : null}
        <div className="flex items-center gap-2">
          <input
            type="color"
            disabled={disabled || followsCategory}
            className="h-9 w-12 cursor-pointer rounded border border-separator bg-card p-0.5 disabled:cursor-not-allowed"
            value={colorForInput(shownFill, layerDefaultFill(shape.layer))}
            onChange={(e) => onUpdate({ fill: e.target.value, use_category_color: false })}
          />
          <input
            className={INPUT}
            disabled={disabled || followsCategory}
            value={followsCategory ? (categoryColor ?? "") : (shape.fill ?? "")}
            placeholder="#rrggbb oder none"
            onChange={(e) => onUpdate({ fill: e.target.value || null, use_category_color: false })}
          />
        </div>
        {shape.layer === "pitch" && !followsCategory ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {PITCH_FILL_PRESETS.map((preset) => (
              <button
                key={preset.fill}
                type="button"
                disabled={disabled}
                title={preset.label}
                onClick={() => onUpdate({ fill: preset.fill, use_category_color: false })}
                className={clsx(
                  "h-6 w-6 rounded-full border-2",
                  shape.fill === preset.fill ? "border-label" : "border-white shadow",
                )}
                style={{ background: preset.fill }}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className={LABEL}>
          Rand
          <input
            className={INPUT}
            disabled={disabled}
            value={shape.stroke ?? ""}
            placeholder="Standard"
            onChange={(e) => onUpdate({ stroke: e.target.value || null })}
          />
        </label>
        <label className={LABEL}>
          Deckkraft {Math.round((shape.opacity ?? 1) * 100)} %
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            disabled={disabled}
            className="min-h-[38px]"
            value={shape.opacity ?? 1}
            onChange={(e) => {
              const v = Number.parseFloat(e.target.value);
              onUpdate({ opacity: v >= 1 ? null : v });
            }}
          />
        </label>
      </div>

      <p className="text-[11px] text-label-tertiary">
        {shape.points.length} Eckpunkte · Fläche ≈ {area.toLocaleString("de-DE")} Einheiten². Eckpunkte im Plan ziehen,
        Mittelpunkte klicken fügt Punkte hinzu, Alt+Klick entfernt einen Punkt.
      </p>
    </div>
  );
}

function RouteProperties({
  route,
  pitches,
  canEdit,
  onUpdate,
  onDelete,
}: {
  route: PlanRoute;
  pitches: Pitch[];
  canEdit: boolean;
  onUpdate: (patch: Partial<PlanRoute>) => void;
  onDelete: () => void;
}) {
  const assigned = route.pitch_id ? pitches.find((p) => p.id === route.pitch_id) : null;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-label">Anfahrt</h3>
        {canEdit ? (
          <button
            type="button"
            className="rounded-md p-1.5 text-status-red hover:bg-fill-tertiary"
            title="Anfahrt löschen"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <label className={LABEL}>
        Gehört zu Platz
        <select
          className={INPUT}
          disabled={!canEdit}
          value={route.pitch_id ?? ""}
          onChange={(e) => onUpdate({ pitch_id: e.target.value || null })}
        >
          <option value="">– noch keinem Platz –</option>
          {pitches.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code}
              {p.label ? ` – ${p.label}` : ""}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-label-tertiary">
          {assigned
            ? `Die Linie von ${assigned.code} wird ausgeblendet. Über die Fläche des Platzes können Sie sie wieder einblenden.`
            : "Ohne Platz bleibt die Linie sichtbar."}
        </span>
      </label>
      <label className={LABEL}>
        Farbe
        <input
          type="color"
          disabled={!canEdit}
          className="h-9 w-12 cursor-pointer rounded border border-separator bg-card p-0.5"
          value={colorForInput(route.color, "#0a84ff")}
          onChange={(e) => onUpdate({ color: e.target.value })}
        />
      </label>
      <p className="text-[11px] text-label-tertiary">
        {route.points.length} Punkte. Ziehen verschiebt die Linie, Eckpunkte verschieben den Verlauf, Mittelpunkte fügen Punkte hinzu, Rechtsklick auf einen Punkt entfernt ihn.
      </p>
    </div>
  );
}

function SymbolProperties({
  symbol,
  canEdit,
  onUpdate,
  onDelete,
}: {
  symbol: PlanSymbol;
  canEdit: boolean;
  onUpdate: (patch: Partial<PlanSymbol>) => void;
  onDelete: () => void;
}) {
  const def = getSymbolDefinition(symbol.type);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-label">
          <SitePlanSymbolPreview type={symbol.type} size={22} />
          Symbol
        </h3>
        {canEdit ? (
          <button
            type="button"
            className="rounded-md p-1.5 text-status-red hover:bg-fill-tertiary"
            title="Symbol löschen"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <label className={LABEL}>
        Typ
        <select
          className={INPUT}
          disabled={!canEdit}
          value={def.type}
          onChange={(e) => onUpdate({ type: e.target.value })}
        >
          {SITE_PLAN_SYMBOLS.map((s) => (
            <option key={s.type} value={s.type}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      <label className={LABEL}>
        Bezeichnung (Tooltip)
        <input
          className={INPUT}
          disabled={!canEdit}
          value={symbol.label ?? ""}
          placeholder={def.label}
          onChange={(e) => onUpdate({ label: e.target.value || null })}
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className={LABEL}>
          Größe
          <input
            type="number"
            min={4}
            max={2000}
            className={INPUT}
            disabled={!canEdit}
            value={Math.round(symbol.size)}
            onChange={(e) => onUpdate({ size: Math.max(4, Number(e.target.value) || 4) })}
          />
        </label>
        <label className={LABEL}>
          Drehung {Math.round(symbol.rotation)}°
          <input
            type="range"
            min={-180}
            max={180}
            step={5}
            className="min-h-[38px]"
            disabled={!canEdit}
            value={symbol.rotation}
            onChange={(e) => onUpdate({ rotation: Number(e.target.value) })}
          />
        </label>
      </div>
      <div className={LABEL}>
        Farbe
        <div className="flex items-center gap-2">
          <input
            type="color"
            disabled={!canEdit}
            className="h-9 w-12 cursor-pointer rounded border border-separator bg-card p-0.5"
            value={colorForInput(symbol.color, def.color)}
            onChange={(e) => onUpdate({ color: e.target.value })}
          />
          <Button type="button" variant="ghost" size="sm" disabled={!canEdit || !symbol.color} onClick={() => onUpdate({ color: null })}>
            Standardfarbe
          </Button>
        </div>
      </div>
    </div>
  );
}

function TextProperties({
  text,
  canEdit,
  onUpdate,
  onDelete,
}: {
  text: PlanText;
  canEdit: boolean;
  onUpdate: (patch: Partial<PlanText>) => void;
  onDelete: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-label">Text</h3>
        {canEdit ? (
          <button
            type="button"
            className="rounded-md p-1.5 text-status-red hover:bg-fill-tertiary"
            title="Text löschen"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <label className={LABEL}>
        Inhalt
        <input
          className={INPUT}
          disabled={!canEdit}
          value={text.text}
          maxLength={120}
          onChange={(e) => onUpdate({ text: e.target.value })}
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className={LABEL}>
          Schriftgröße
          <input
            type="number"
            min={4}
            max={500}
            className={INPUT}
            disabled={!canEdit}
            value={Math.round(text.size)}
            onChange={(e) => onUpdate({ size: Math.max(4, Number(e.target.value) || 4) })}
          />
        </label>
        <label className={LABEL}>
          Drehung {Math.round(text.rotation)}°
          <input
            type="range"
            min={-180}
            max={180}
            step={5}
            className="min-h-[38px]"
            disabled={!canEdit}
            value={text.rotation}
            onChange={(e) => onUpdate({ rotation: Number(e.target.value) })}
          />
        </label>
      </div>
      <div className={LABEL}>
        Farbe
        <div className="flex items-center gap-2">
          <input
            type="color"
            disabled={!canEdit}
            className="h-9 w-12 cursor-pointer rounded border border-separator bg-card p-0.5"
            value={colorForInput(text.color, "#111827")}
            onChange={(e) => onUpdate({ color: e.target.value })}
          />
          <Button type="button" variant="ghost" size="sm" disabled={!canEdit || !text.color} onClick={() => onUpdate({ color: null })}>
            Standardfarbe
          </Button>
        </div>
      </div>
    </div>
  );
}
